import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AKSI_LABEL, formatTanggal, getApiBaseUrl, labelStatus } from './langgananUtil';
import { waktuCabang } from '../../utils/waktuCabang';

// Dialog superadmin: atur paket, masa aktif (permanen / tanggal / tambah hari), dan lihat riwayat.
const LanggananDialog = ({ open, cabang, pakets, onClose, onSaved }) => {
  const [paketUuid, setPaketUuid] = useState('');
  const [mode, setMode] = useState('tanggal'); // 'permanen' | 'tanggal'
  const [tanggal, setTanggal] = useState('');
  const [catatan, setCatatan] = useState('');
  const [riwayat, setRiwayat] = useState([]);
  const [langganan, setLangganan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const muat = async () => {
    try {
      const res = await axios.get(`${getApiBaseUrl()}/cabang/${cabang.uuid}/langganan`);
      const lg = res.data.data.langganan;
      setLangganan(lg);
      setRiwayat(res.data.data.riwayat || []);
      setPaketUuid(lg.paketUuid || '');
      setMode(lg.masaAktifSampai ? 'tanggal' : 'permanen');
      setTanggal(lg.masaAktifSampai || '');
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal memuat data langganan');
    }
  };

  useEffect(() => {
    if (open && cabang) {
      setError('');
      setCatatan('');
      muat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cabang?.uuid]);

  const kirim = async (payload) => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${getApiBaseUrl()}/cabang/${cabang.uuid}/langganan`, { ...payload, catatan: catatan || undefined });
      await muat();
      setCatatan('');
      if (onSaved) onSaved();
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const simpan = () => {
    if (!paketUuid) {
      setError('Pilih paket untuk cabang ini');
      return;
    }
    if (mode === 'tanggal' && !tanggal) {
      setError('Isi tanggal masa aktif atau pilih Permanen');
      return;
    }
    kirim({ paketUuid, masaAktifSampai: mode === 'permanen' ? null : tanggal });
  };

  if (!cabang) return null;
  const status = labelStatus(langganan);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Langganan: {cabang.namacabang}{' '}
        {langganan && <Chip size="small" label={status.text} color={status.color} sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormControl fullWidth margin="dense">
          <InputLabel id="paket-label">Paket</InputLabel>
          <Select labelId="paket-label" label="Paket" value={paketUuid} onChange={(e) => setPaketUuid(e.target.value)}>
            {pakets.map((p) => (
              <MenuItem key={p.uuid} value={p.uuid}>{p.nama}{p.semuaFitur ? ' (semua fitur)' : ''}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Masa aktif</Typography>
        <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel value="tanggal" control={<Radio />} label="Berakhir pada tanggal" />
          <FormControlLabel value="permanen" control={<Radio />} label="Permanen (tanpa expired)" />
        </RadioGroup>
        {mode === 'tanggal' && (
          <TextField
            type="date"
            fullWidth
            margin="dense"
            label="Berakhir pada (terkunci mulai tanggal ini)"
            InputLabelProps={{ shrink: true }}
            value={tanggal}
            onChange={(e) => setTanggal(e.target.value)}
          />
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
          <Typography variant="body2" sx={{ alignSelf: 'center', mr: 1 }}>Perpanjang cepat:</Typography>
          {[30, 90, 180, 365].map((h) => (
            <Button key={h} size="small" variant="outlined" disabled={saving} onClick={() => kirim({ tambahHari: h })}>
              +{h} hari
            </Button>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Perpanjangan dihitung dari tanggal berakhir bila masih aktif, atau dari hari ini bila sudah expired atau permanen. Pada tanggal berakhir cabang sudah terkunci.
        </Typography>

        <TextField
          fullWidth
          margin="normal"
          label="Catatan (opsional, masuk riwayat)"
          value={catatan}
          onChange={(e) => setCatatan(e.target.value)}
        />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Riwayat langganan</Typography>
        {riwayat.length === 0 && <Typography variant="body2" color="text.secondary">Belum ada riwayat.</Typography>}
        <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
          {riwayat.map((r) => (
            <Box key={r.uuid} sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {AKSI_LABEL[r.aksi] || r.aksi}{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  {waktuCabang(r.createdAt).toLocaleString('id-ID')} oleh {r.oleh || '-'}
                </Typography>
              </Typography>
              {(r.paketLama || r.paketBaru) && r.paketLama !== r.paketBaru && (
                <Typography variant="caption" display="block">Paket: {r.paketLama || '-'} → {r.paketBaru || '-'}</Typography>
              )}
              {r.masaAktifLama !== r.masaAktifBaru && (
                <Typography variant="caption" display="block">
                  Masa aktif: {r.masaAktifLama ? formatTanggal(r.masaAktifLama) : 'Permanen'} → {r.masaAktifBaru ? formatTanggal(r.masaAktifBaru) : 'Permanen'}
                </Typography>
              )}
              {r.catatan && <Typography variant="caption" display="block">Catatan: {r.catatan}</Typography>}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Tutup</Button>
        <Button variant="contained" onClick={simpan} disabled={saving}>Simpan</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LanggananDialog;
